import { Link } from "react-router-dom";
import { Croissant, Cake, Cookie, Wheat } from "lucide-react";
import { Reveal, Overline } from "@/components/Motion";

const HIGHLIGHTS = [
  { icon: Cake, title: "Celebration Cakes", desc: "Chocolate truffle, Belgian, London & fruit cakes — plus fully customised cakes made to your design." },
  { icon: Croissant, title: "Pastries & Tea Cakes", desc: "Choco almond, mango and rainbow pastries; dates-almond, pista mawa and marble loaves, fresh every morning." },
  { icon: Cookie, title: "Hand-Crafted Cookies", desc: "Honey ragi oats, Karachi, almond sticks, double chocolate and honey muesli — baked in small batches." },
  { icon: Wheat, title: "Fresh Breads Daily", desc: "Sandwich, multigrain, honey oats and baguettes, plus buns and pao — order two hours ahead, warm from the oven." },
];

const IMAGES = [
  { src: "https://images.unsplash.com/photo-1509440159596-0249088772ff?q=85&w=900&auto=format&fit=crop", alt: "Fresh artisan breads from the Forco Amore bakery", cls: "aspect-[3/4] mt-10" },
  { src: "https://images.unsplash.com/photo-1578985545062-69928b1d9587?q=85&w=900&auto=format&fit=crop", alt: "Chocolate truffle cake from our bakery", cls: "aspect-[3/4]" },
  { src: "https://images.unsplash.com/photo-1499636136210-6f4ee915583e?q=85&w=900&auto=format&fit=crop", alt: "Hand-crafted cookies baked in-house", cls: "aspect-[3/4] mt-16" },
];

const Bakery = () => (
  <section id="bakery" className="bg-shell border-y border-sand py-28 sm:py-36 overflow-hidden" data-testid="bakery-section">
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <div className="grid gap-14 lg:grid-cols-[1.05fr_1fr] items-center">
        <div>
          <Reveal>
            <Overline className="text-wine">The In-House Bakery</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5">
              From Our Ovens, <span className="italic text-wine">With Amore</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-xl text-faded text-base sm:text-lg font-light leading-relaxed">
              Before the first table is set, our bakers are already at work. Every cake, cookie,
              pastry and loaf that leaves Forco Amore is baked under our own roof — no shortcuts,
              no store-bought anything.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={0.15 + i * 0.08}>
                <div className="flex gap-4" data-testid={`bakery-highlight-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-wine/25 text-wine">
                    <h.icon size={18} strokeWidth={1.4} />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl font-medium tracking-tight">{h.title}</h3>
                    <p className="mt-1.5 text-sm text-faded font-light leading-relaxed">{h.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link
                to="/menu?cat=bakery"
                data-testid="bakery-menu-btn"
                className="rounded-full bg-wine px-9 py-4 text-sm tracking-[0.14em] uppercase text-cream hover:bg-winedark hover:scale-[1.03] active:scale-95 transition-all duration-300"
              >
                Explore the Bakery Menu
              </Link>
              <Link
                to="/contact"
                data-testid="bakery-order-btn"
                className="rounded-full border border-wine px-9 py-4 text-sm tracking-[0.14em] uppercase text-wine hover:bg-wine hover:text-cream transition-colors duration-300"
              >
                Order a Custom Cake
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-3 gap-4 items-start">
          {IMAGES.map((img, i) => (
            <Reveal key={img.src} delay={0.15 + i * 0.1}>
              <div className={`overflow-hidden rounded-[1.5rem] ${img.cls}`}>
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="img-lux h-full w-full object-cover"
                  data-testid={`bakery-image-${i}`}
                />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default Bakery;
